import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { setTracker } from "../client";
import type { AnalyticsEvent } from "../types";

type Config = {
    endpoint: string;
    batchSize?: number;
    flushIntervalMs?: number;
    respectDoNotTrack?: boolean;
    getAuthToken?: () => Promise<string | undefined>;
    getUserId?: () => string | undefined;
    getUserEmail: () => string | undefined;
};

type Ctx = {
    track: <M extends Record<string, unknown> = Record<string, unknown>>(
        // eslint-disable-next-line no-unused-vars
        evt: AnalyticsEvent<M>
    ) => void;
    sessionId: string;
    anonymousId: string;
    enabled: boolean;
    // eslint-disable-next-line no-unused-vars
    setEnabled: (v: boolean) => void;
};

const AnalyticsCtx = createContext<Ctx | null>(null);

const makeUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

function getOrCreateAnonId(key = "analytics:anonId") {
    try {
        const existing = localStorage.getItem(key);
        if (existing) return existing;
        const id = makeUuid();
        localStorage.setItem(key, id);
        return id;
    } catch {
        return makeUuid();
    }
}

export const AnalyticsProvider: React.FC<
    React.PropsWithChildren<{ config: Config }>
> = ({ config, children }) => {
    const batchSize = config.batchSize ?? 10;
    const flushIntervalMs = config.flushIntervalMs ?? 4000;
    const respectDNT = config.respectDoNotTrack ?? true;

    const [enabled, setEnabled] = useState<boolean>(() => {
        if (
            respectDNT &&
            typeof navigator !== "undefined" &&
            (navigator as any).doNotTrack === "1"
        )
            return false;
        try {
            const v = localStorage.getItem("analytics:enabled");
            return v === null ? true : v === "true";
        } catch {
            return true;
        }
    });

    // persist enabled flag
    useEffect(() => {
        try {
            localStorage.setItem("analytics:enabled", String(enabled));
        } catch {
            //
        }
    }, [enabled]);

    const sessionIdRef = useRef<string>(makeUuid());
    const anonymousIdRef = useRef<string>(getOrCreateAnonId());

    const queueRef = useRef<any[]>([]);
    const sendingRef = useRef(false);

    // ---- track(): generic signature matches client AnalyticsFn exactly
    const track = useCallback(
        <M extends Record<string, unknown> = Record<string, unknown>>(
            evt: AnalyticsEvent<M>
        ) => {
            if (!enabled) return;
            const nowIso = new Date().toISOString();
            const enriched = {
                ...evt,
                clientTs: nowIso,
                pageUrl:
                    evt.pageUrl ??
                    (typeof window !== "undefined"
                        ? window.location.href
                        : undefined),
                referrer:
                    evt.referrer ??
                    (typeof document !== "undefined"
                        ? document.referrer
                        : undefined),
                userAgent:
                    typeof navigator !== "undefined"
                        ? navigator.userAgent
                        : undefined,
                sessionId: sessionIdRef.current,
                anonymousId: anonymousIdRef.current,
                eventId: makeUuid(),
            };
            queueRef.current.push(enriched);
            if (queueRef.current.length >= batchSize) {
                void flush();
            }
        },
        [enabled, batchSize]
    );

    // ---- flush(): send queued events
    const flush = useCallback(async () => {
        if (sendingRef.current || queueRef.current.length === 0 || !enabled)
            return;

        sendingRef.current = true;
        try {
            const userId = config.getUserId?.();
            const userEmail = config.getUserEmail?.();
            const token = await config.getAuthToken?.();

            while (queueRef.current.length) {
                const batch = queueRef.current.splice(0, batchSize);
                // console.log("payload for analytics provider", userId, batch);
                // attach userId to each event, remove anonymousId if present
                const batchWithUser = batch.map((e) => {
                    const { ...rest } = e ?? {};
                    return {
                        ...rest,
                        ...(userId ? { userId } : {}),
                        ...(userEmail ? { userEmail } : {}),
                    };
                });

                const res = await fetch(config.endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(batchWithUser),
                    keepalive: true,
                    // credentials: "include", // only if you truly need cookies
                });

                if (!res.ok) {
                    queueRef.current.unshift(...batch);
                    break;
                }
            }
        } catch {
            // swallow; events remain if reinserted
        } finally {
            sendingRef.current = false;
        }
    }, [batchSize, config, enabled]);

    // register the tracker so non-React code can call track()
    useEffect(() => {
        setTracker(track); // <-- no TS error because signatures match
    }, [track]);

    // interval flush
    useEffect(() => {
        if (!enabled) return;
        const id = setInterval(() => void flush(), flushIntervalMs);
        return () => clearInterval(id);
    }, [enabled, flush, flushIntervalMs]);

    // unload/visibility flush
    useEffect(() => {
        const onHidden = () => {
            if (!enabled || queueRef.current.length === 0) return;
            try {
                const userId = config.getUserId?.();
                const payload = JSON.stringify({
                    userId,
                    events: queueRef.current.splice(0),
                });
                const ok = navigator.sendBeacon?.(
                    config.endpoint,
                    new Blob([payload], { type: "application/json" })
                );
                if (!ok) {
                    fetch(config.endpoint, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: payload,
                        keepalive: true,
                    }).catch(() => {
                        //
                    });
                }
            } catch {
                //
            }
        };
        document.addEventListener("visibilitychange", onHidden);
        window.addEventListener("beforeunload", onHidden);
        return () => {
            document.removeEventListener("visibilitychange", onHidden);
            window.removeEventListener("beforeunload", onHidden);
        };
    }, [enabled, config.endpoint, config.getUserId]);

    const ctx = useMemo<Ctx>(
        () => ({
            track,
            sessionId: sessionIdRef.current,
            anonymousId: anonymousIdRef.current,
            enabled,
            setEnabled,
        }),
        [track, enabled]
    );

    return (
        <AnalyticsCtx.Provider value={ctx}>{children}</AnalyticsCtx.Provider>
    );
};

export function useAnalytics(): Ctx {
    const ctx = useContext(AnalyticsCtx);
    if (!ctx)
        throw new Error("useAnalytics must be used within <AnalyticsProvider>");
    return ctx;
}
